import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment-timezone';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { DATE_TIME_FORMAT, VALIDATION_SCHEMAS } from '../../../constants';
import { BasicModal, Button, ErrorMessage } from '../../Shared';

export const CallNotesModal = ({
  open,
  formObjNotes,
  onClose,
  onChangeFormObjNotes,
  onChangeFormCallNotes,
}) => {
  const defaultValues = {
    callNotes: formObjNotes.callNotes,
  };

  const {
    control,
    formState: { isDirty },
    getValues,
    setValue,
    setError,
    handleSubmit,
  } = useForm({
    resolver: yupResolver(VALIDATION_SCHEMAS.CALL_NOTES),
    mode: 'onChange',
    defaultValues,
  });

  const setTimeStamps = () => {
    const timeStamps = moment().format(
      DATE_TIME_FORMAT.MM_DD_YYYY_h_mm_A_GMT_Z,
    );
    const prevCallNotes = getValues('callNotes');
    setValue(
      'callNotes',
      prevCallNotes ? prevCallNotes + '\n' + timeStamps : timeStamps,
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = handleSubmit(async data => {
    setIsSubmitting(true);
    const nextFormObjNotes = { ...formObjNotes, callNotes: data.callNotes };

    try {
      const response = await fetch('/call-options-notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nextFormObjNotes),
      });

      if (response.status === 400) {
        const invalidFields = await response.json();
        invalidFields?.validationErrors?.forEach(invalidField => {
          Object.entries(invalidField).forEach(([field, errors]) => {
            setError(field, { message: errors[0] });
          });
        });
        setIsSubmitting(false);
        return;
      }

      if (response.status !== 200) throw new Error();

      onChangeFormObjNotes(nextFormObjNotes);
      onChangeFormCallNotes(nextFormObjNotes.callNotes);
      setIsSubmitting(false);
      onClose();
    } catch {
      setIsSubmitting(false);
      toast.error("Can't save, please try again!", {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  });

  const isSaveButtonDisabled = isSubmitting || !isDirty;

  return (
    <BasicModal open={open} onClose={isSubmitting ? undefined : onClose}>
      <form onSubmit={onSubmit}>
        <div className="bg-white w-[576px] rounded-2xl shadow-[-40px_40px_80px_-8px_rgba(0,0,0,0.24)]">
          <div className="flex items-center justify-between border-b-[2px] border-b-secondary/20 px-4 py-3">
            <h5 className="text-xl font-semibold">Call Notes</h5>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-transparent text-sm font-semibold text-gray-800 transition hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50 dark:text-white dark:hover:bg-gray-700 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
              data-hs-overlay="#call-notes-modal"
            >
              <span className="sr-only">Close</span>
              <svg
                className="h-4 w-4 flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
          <div className="overflow-y-auto p-4">
            <Controller
              control={control}
              name="callNotes"
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                const hasErrorMessage = !!error?.message;
                const borderColor = hasErrorMessage
                  ? 'border-red-500/20 focus:border-red-500'
                  : 'border-secondary/20 focus:border-blue-500';
                const textareaClassName = `${borderColor} block resize-none w-full overflow-y-auto rounded-lg border-2 px-4 py-3 text-lg font-medium transition focus:outline-none focus:ring-2 focus:ring-blue-500/20 [&::-webkit-scrollbar-thumb]:bg-secondary/80 [&::-webkit-scrollbar-track]:bg-secondary/20 [&::-webkit-scrollbar]:w-2`;

                return (
                  <>
                    <textarea
                      rows={5}
                      className={textareaClassName}
                      placeholder="Start typing here..."
                      autoFocus={true}
                      onChange={onChange}
                      value={value}
                    />
                    {hasErrorMessage && (
                      <ErrorMessage errorMessage={error.message} />
                    )}
                  </>
                );
              }}
            />
          </div>
          <div className="flex items-center justify-between p-4">
            <button
              onClick={setTimeStamps}
              type="button"
              className="inline-flex items-center justify-center gap-x-2 rounded-full border border-accent-2 bg-transparent px-6 py-2 text-lg font-medium text-accent-2 transition hover:border-accent-2 hover:bg-accent-2 hover:text-white focus:outline-none focus:ring focus:ring-primary/20 disabled:pointer-events-none disabled:opacity-50"
            >
              Time Stamp
            </button>
            <Button title="Save" disabled={isSaveButtonDisabled} />
          </div>
        </div>
      </form>
    </BasicModal>
  );
};
